/* ---------------------------------------------------------------------
 *                           Import Packages
 * --------------------------------------------------------------------- */

// React
import React, { useState } from "react";

// MUI
import { Dialog, Button, DialogContent } from "@mui/material";

// Components
import FormTabs from "./FormTabs";
import RegisterForm from "./RegisterForm";
import { SiJiaSnack } from "../common/Common";
import MessageLoginForm from "./MessageLoginForm";
import PasswordLoginForm from "./PasswordLoginForm";
import ResetPasswordForm from "./ResetPasswordForm";

/* ---------------------------------------------------------------------
 *                           LoginDialog Component
 * --------------------------------------------------------------------- */

/**
 * 定义登录对话框组件
 * @param {boolean} param.isOpenDialog - 是否显示对话框
 * @param {function} param.onSubmit - 提交表单的回调函数
 * @param {function} param.CloseDialog - 关闭对话框的回调函数
 * @function LoginDialog
 * @returns 登录对话框组件
 */
export default function LoginDialog({ isOpenDialog, onSubmit, onCloseDialog }) {
  /* -------------------------------------------
   *                 Form Data
   * -------------------------------------------*/

  /**
   * 表单数据
   * @type {object}
   * @param {string} phone 手机号
   * @param {string} password 密码
   * @param {string} newPassword 新密码
   * @param {string} verificationCode 验证码
   */
  const [formData, setFormData] = useState({
    phone: "",
    password: "",
    newPassword: "",
    verificationCode: "",
  });

  /**
   * 处理表单输入变化
   * @param {object} e - 事件对象
   */
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  /**
   * 重置表单数据
   * @returns {void}
   */
  const resetForm = () => {
    setFormData({
      phone: "",
      password: "",
      newPassword: "",
      verificationCode: "",
    });
  };

  /* -------------------------------------------
   *                 Form Mode
   * -------------------------------------------*/
  /**
   * 模式（login: 登录；register: 注册：resetPassword: 重设密码）
   * @type {string}
   */
  const [mode, setMode] = useState("login");

  /**
   * 切换模式（登录/注册/重设密码）
   * @param {string} newMode - 新的模式
   * @returns {void}
   */
  const handleModeChange = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  /* -------------------------------------------
   *                  Snackbar
   * -------------------------------------------*/
  /**
   * 显示Snackbar
   * @type {Object}
   */
  const [snackbarMes, setSnackbarMes] = useState({
    open: false,
    text: "",
    severity: "warning",
  });

  /**
   * 处理Snackbar关闭事件
   * @param {string} reason - 关闭原因
   * @returns {Function}
   */
  const handleSnackbarClose = (reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarMes((prev) => ({ ...prev, open: false }));
  };

  /* -------------------------------------------
   *                 Login Type
   * -------------------------------------------*/

  /**
   * 登录类型（message：短信登录；password：密码登录）
   * @type {string}
   * @default message
   */
  const [loginType, setLoginType] = useState("message");
  /**
   * 切换登录类型
   * @param {object} e - 事件对象
   * @param {string} newValue - 新的登录类型
   */
  const handleLoginTypeChange = (e, newValue) => {
    resetForm();
    setLoginType(newValue === 0 ? "message" : "password");
  };

  /* -------------------------------------------
   *                 Send Code
   * -------------------------------------------*/
  /**
   * 验证码按钮状态
   * @type {number}
   */
  const [countdown, setCountdown] = useState(0);

  /**
   * 发送验证码
   * @returns {void}
   */
  const handleSendCode = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!formData.phone || countdown > 0) {
      return;
    }

    try {
      const result = await onSubmit("sendCode", { mobile: formData.phone });

      if (result?.success) {
        setCountdown(60);
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setSnackbarMes({
          open: true,
          text: result.message || "发送验证码失败",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbarMes({
        open: true,
        text: "发送验证码失败，请稍后重试",
        severity: "error",
      });
    }
  };

  /**
   * 处理表单提交 (登录、注册或重置密码)
   * 这个函数会调用外部传入的 onSubmit prop，并在操作完成后重置表单并显示Snackbar提示。
   * @param {string} type - 提交类型 ('login', 'register', 'resetPassword')
   * @param {object} userObj - 用户数据对象
   * @returns {Promise<void>}
   */
  const handleFormSubmission = async (type, userObj) => {
    try {
      await onSubmit(type, userObj);
      if (type === "resetPassword" || type === "register") {
        resetForm();
      }

    } catch (error) {
      setSnackbarMes({
        open: true,
        text: error.message || "发生未知错误，请稍后重试。",
        severity: "error",
      });
      resetForm();
    }
  };

  /* ---------------------------------------------------------------------
   *                            Render Component
   * --------------------------------------------------------------------- */

  return (
    <>
      <Dialog
        open={isOpenDialog}
        onClose={onCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogContent sx={{ p: 4, position: "relative" }}>
          <Button
            onClick={() => {
              setMode("login");
              setLoginType("message");
              resetForm();
              onCloseDialog();
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              minWidth: "auto",
              color: "#999",
            }}
          >
            ✕
          </Button>

          <FormTabs
            mode={mode}
            loginType={loginType}
            onTabChange={handleLoginTypeChange}
          />

          {mode === "login" ? (
            <>
              {loginType === "message" ? (
                // 短信登录
                <MessageLoginForm
                  formData={formData}
                  countdown={countdown}
                  onSubmit={handleFormSubmission}
                  onSendCode={handleSendCode}
                  onModeChange={handleModeChange}
                  onInputChange={handleInputChange}
                />
              ) : (
                // 密码登录
                <PasswordLoginForm
                  formData={formData}
                  onSubmit={handleFormSubmission}
                  onModeChange={handleModeChange}
                  onInputChange={handleInputChange}
                />
              )}
            </>
          ) : mode === "register" ? (
            // 注册表单
            <RegisterForm
              formData={formData}
              countdown={countdown}
              onSubmit={handleFormSubmission} 
              onSendCode={handleSendCode}
              onInputChange={handleInputChange}
              onModeChange={handleModeChange}
            />
          ) : (
            // 重设密码
            <ResetPasswordForm
              formData={formData}
              countdown={countdown}
              onSubmit={handleFormSubmission} 
              onSendCode={handleSendCode}
              onInputChange={handleInputChange}
              onModeChange={handleModeChange}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <SiJiaSnack
        open={snackbarMes.open}
        text={snackbarMes.text}
        severity={snackbarMes.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
}
